import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, TrendingUp, Tag, ShoppingBag, Megaphone, Users,
  Loader2, ArrowLeft, Zap, RotateCcw, Clock, ChevronDown, ChevronUp,
  Target, BarChart3, Flame, Lightbulb, Trophy
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Suggestion {
  category: "promocao" | "combo" | "marketing" | "reativacao";
  title: string;
  description: string;
  impact: "alto" | "medio" | "baixo";
  effort: "facil" | "medio" | "dificil";
  metric_target: string;
}

interface AnalysisResult {
  analysis_summary: string;
  suggestions: Suggestion[];
}

interface HistoryEntry {
  id: string;
  created_at: string;
  analysis_summary: string;
  suggestions: Suggestion[];
}

const categoryConfig = {
  promocao: { label: "Promoção", icon: Tag, color: "text-orange-500", bg: "bg-orange-500/10" },
  combo: { label: "Combo", icon: ShoppingBag, color: "text-blue-500", bg: "bg-blue-500/10" },
  marketing: { label: "Marketing", icon: Megaphone, color: "text-purple-500", bg: "bg-purple-500/10" },
  reativacao: { label: "Reativação", icon: Users, color: "text-emerald-500", bg: "bg-emerald-500/10" },
};

const impactConfig = {
  alto: { label: "Alto impacto", color: "bg-red-500/10 text-red-600 border-red-500/20" },
  medio: { label: "Médio impacto", color: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20" },
  baixo: { label: "Baixo impacto", color: "bg-green-500/10 text-green-600 border-green-500/20" },
};

const effortConfig = {
  facil: { label: "Fácil", icon: "⚡" },
  medio: { label: "Médio", icon: "🔧" },
  dificil: { label: "Difícil", icon: "🏗️" },
};

const Crescimento = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  const loadHistory = useCallback(async (restId: string) => {
    const { data } = await (supabase
      .from("growth_suggestions" as any)
      .select("*")
      .eq("restaurant_id", restId)
      .order("created_at", { ascending: false })
      .limit(10) as any);
    if (data && Array.isArray(data)) {
      const entries = data.map((d: any) => ({
        id: d.id,
        created_at: d.created_at,
        analysis_summary: d.analysis_summary || "",
        suggestions: (d.suggestions as Suggestion[]) || [],
      }));
      setHistory(entries);
      if (entries.length > 0 && !result) {
        setResult({
          analysis_summary: entries[0].analysis_summary,
          suggestions: entries[0].suggestions,
        });
      }
    }
  }, [result]);

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/login"); return; }

      const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
      let rest: any = null;
      if (impersonateId) {
        const { data } = await supabase.from("restaurants").select("id").eq("id", impersonateId).maybeSingle();
        rest = data;
      } else {
        const { data } = await supabase.from("restaurants").select("id").eq("owner_id", user.id).maybeSingle();
        rest = data;
      }
      if (!rest) { navigate("/dashboard"); return; }
      setRestaurantId(rest.id);
      await loadHistory(rest.id);
      setLoading(false);
    };
    init();
  }, [navigate, loadHistory]);

  const runAnalysis = async () => {
    if (!restaurantId) return;
    setAnalyzing(true);
    try {
      const { data, error } = await supabase.functions.invoke("analyze-growth", {
        body: { restaurantId },
      });
      if (error) throw error;
      if (data?.suggestions) {
        setResult(data);
        await loadHistory(restaurantId);
        toast({ title: "Análise concluída!", description: `${data.suggestions.length} sugestões geradas.` });
      }
    } catch (e: any) {
      toast({ title: "Erro na análise", description: e?.message || "Tente novamente", variant: "destructive" });
    } finally {
      setAnalyzing(false);
    }
  };

  const filteredSuggestions = result?.suggestions?.filter(
    s => !activeFilter || s.category === activeFilter
  ) || [];

  const categoryCounts = result?.suggestions?.reduce((acc, s) => {
    acc[s.category] = (acc[s.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-xl border-b border-border">
        <div className="flex items-center justify-between px-4 md:px-6 h-16">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-lg font-bold">Modo Crescimento</h1>
                <p className="text-xs text-muted-foreground">IA analisa e sugere ações para seu restaurante</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {history.length > 0 && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowHistory(!showHistory)}>
                <Clock className="h-3.5 w-3.5" />
                Histórico ({history.length})
              </Button>
            )}
            <Button
              onClick={runAnalysis}
              disabled={analyzing}
              className="gap-2 bg-gradient-to-r from-primary to-primary/80"
            >
              {analyzing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Analisando...
                </>
              ) : (
                <>
                  {result ? <RotateCcw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
                  {result ? "Nova Análise" : "Analisar Meu Negócio"}
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      <main className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
        {/* History dropdown */}
        <AnimatePresence>
          {showHistory && history.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Clock className="h-4 w-4" /> Análises Anteriores
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {history.map((h) => (
                    <button
                      key={h.id}
                      onClick={() => {
                        setResult({ analysis_summary: h.analysis_summary, suggestions: h.suggestions });
                        setShowHistory(false);
                        setActiveFilter(null);
                      }}
                      className="w-full text-left p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-muted-foreground">
                          {new Date(h.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </span>
                        <Badge variant="secondary" className="text-[10px]">{h.suggestions.length} sugestões</Badge>
                      </div>
                      <p className="text-sm line-clamp-2">{h.analysis_summary}</p>
                    </button>
                  ))}
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty state */}
        {!result && !analyzing && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Lightbulb className="h-10 w-10 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Descubra como crescer mais</h2>
            <p className="text-muted-foreground max-w-md mx-auto mb-8">
              Nossa IA analisa seus pedidos, cardápio, avaliações e clientes para gerar sugestões personalizadas de crescimento.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto mb-8">
              {Object.entries(categoryConfig).map(([key, cfg]) => (
                <div key={key} className={`p-4 rounded-xl ${cfg.bg} text-center`}>
                  <cfg.icon className={`h-6 w-6 ${cfg.color} mx-auto mb-2`} />
                  <span className="text-sm font-medium">{cfg.label}</span>
                </div>
              ))}
            </div>
            <Button onClick={runAnalysis} size="lg" className="gap-2 bg-gradient-to-r from-primary to-primary/80 h-12 px-8">
              <Sparkles className="h-5 w-5" />
              Analisar Meu Negócio
            </Button>
          </motion.div>
        )}

        {/* Analyzing state */}
        {analyzing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6 animate-pulse">
              <BarChart3 className="h-10 w-10 text-primary" />
            </div>
            <h2 className="text-xl font-bold mb-2">Analisando seu restaurante...</h2>
            <p className="text-muted-foreground">Coletando dados de pedidos, cardápio, avaliações e clientes.</p>
          </motion.div>
        )}

        {/* Results */}
        {result && !analyzing && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* Summary */}
            <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                    <Trophy className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Resumo da Análise</h3>
                    <p className="text-sm text-muted-foreground">{result.analysis_summary}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Category filters */}
            <div className="flex gap-2 overflow-x-auto pb-1">
              <button
                onClick={() => setActiveFilter(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  !activeFilter ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
                }`}
              >
                Todas ({result.suggestions.length})
              </button>
              {Object.entries(categoryConfig).map(([key, cfg]) => {
                const count = categoryCounts[key] || 0;
                if (count === 0) return null;
                return (
                  <button
                    key={key}
                    onClick={() => setActiveFilter(activeFilter === key ? null : key)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                      activeFilter === key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <cfg.icon className="h-3.5 w-3.5" />
                    {cfg.label} ({count})
                  </button>
                );
              })}
            </div>

            {/* Suggestion cards */}
            <div className="grid md:grid-cols-2 gap-4">
              <AnimatePresence mode="popLayout">
                {filteredSuggestions.map((s, i) => {
                  const catCfg = categoryConfig[s.category];
                  const impCfg = impactConfig[s.impact];
                  const effCfg = effortConfig[s.effort];
                  const isExpanded = expandedCard === i;

                  return (
                    <motion.div
                      key={`${s.category}-${i}`}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ delay: i * 0.03 }}
                    >
                      <Card
                        className="cursor-pointer hover:shadow-md transition-all border-border hover:border-primary/20"
                        onClick={() => setExpandedCard(isExpanded ? null : i)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className={`w-9 h-9 rounded-lg ${catCfg.bg} flex items-center justify-center shrink-0`}>
                              <catCfg.icon className={`h-4 w-4 ${catCfg.color}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="text-[10px]">{catCfg.label}</Badge>
                                <Badge variant="outline" className={`text-[10px] ${impCfg.color}`}>{impCfg.label}</Badge>
                              </div>
                              <h4 className="font-semibold text-sm mb-1">{s.title}</h4>
                              <p className={`text-xs text-muted-foreground ${isExpanded ? "" : "line-clamp-2"}`}>
                                {s.description}
                              </p>
                              {isExpanded && (
                                <motion.div
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  className="mt-3 pt-3 border-t border-border flex items-center gap-4"
                                >
                                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                    <Target className="h-3.5 w-3.5" />
                                    {s.metric_target}
                                  </div>
                                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <span>{effCfg.icon}</span>
                                    {effCfg.label}
                                  </div>
                                </motion.div>
                              )}
                            </div>
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default Crescimento;
